'use client'
import {
  useId,
  useState,
  useTransition,
  type ChangeEvent,
  type FC,
  type FormEvent,
  type ReactNode,
} from 'react'
import { useRouter } from 'next/navigation'
import { CircularProgress, TextField } from '@achieve/ascend'
import { Button } from 'components/Button'
import { Typography as TypographyJs } from 'components/Typography'
import {
  INVALID_PASSWORD_MESSAGE,
  LOCKED_MESSAGE,
  UNLOCK_ERROR_MESSAGE,
} from 'lib/passwordProtection/constants'

// `Typography` is a JS component without TS definitions.
const Typography = TypographyJs as unknown as FC<{
  children: ReactNode
  id?: string
  variant?: string
  component?: string
  className?: string
  'data-testid'?: string
}>

const UNLOCK_ENDPOINT = '/api/content-password/unlock'

interface PasswordGateFormProps {
  entryId: string
  label?: string
  placeholder?: string
  submitLabel?: string
}

export function PasswordGateForm({
  entryId,
  label = 'Password',
  placeholder = 'Enter your password',
  submitLabel = 'Enter',
}: PasswordGateFormProps) {
  const router = useRouter()
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLocked, setIsLocked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isRefreshing, startRefresh] = useTransition()
  const inputId = useId()
  const errorId = `${inputId}-error`

  const isBusy = isSubmitting || isRefreshing

  // Editing the field clears the previous verdict: the message only ever
  // describes the attempt the visitor last submitted. A lockout stays until reload.
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isLocked) return
    setPassword(event.target.value)
    setError(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isBusy || isLocked || !password) return

    setError(null)
    setIsSubmitting(true)

    try {
      const response = await fetch(UNLOCK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entryId, password }),
      })

      if (!response.ok) {
        const locked = response.status === 429
        setIsLocked(locked)
        setError(
          locked
            ? LOCKED_MESSAGE
            : response.status === 401
            ? INVALID_PASSWORD_MESSAGE
            : UNLOCK_ERROR_MESSAGE
        )
        setPassword('')
        return
      }

      // The unlock cookie is httpOnly, so only a server re-render can reveal the
      // protected content.
      startRefresh(() => router.refresh())
    } catch {
      setError(UNLOCK_ERROR_MESSAGE)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      className="flex flex-col items-center w-full gap-2"
      onSubmit={handleSubmit}
      data-testid="password-gate-form"
      noValidate
    >
      <div className="flex flex-col w-full max-w-[320px] gap-1">
        <label htmlFor={inputId}>
          <Typography variant="labelMd" component="span">
            {label}
          </Typography>
        </label>
        <TextField
          // The gate is centered, but the field's own text reads from the left.
          className="[&_input]:text-left"
          id={inputId}
          data-testid="password-gate-input"
          type="password"
          name="password"
          value={password}
          onChange={handleChange}
          placeholder={placeholder}
          autoComplete="off"
          disabled={isBusy || isLocked}
          error={Boolean(error)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          fullWidth
        />
      </div>

      <div className="min-h-[24px] w-full max-w-[320px]" aria-live="polite">
        {error && (
          <Typography
            id={errorId}
            className="text-red-600"
            variant="bodySm"
            component="p"
            data-testid="password-gate-error"
          >
            {error}
          </Typography>
        )}
      </div>

      <Button
        type="submit"
        color="primary"
        variant="contained"
        disabled={isBusy || isLocked || !password}
        aria-disabled={isBusy || isLocked}
        aria-busy={isBusy}
        data-testid="password-gate-submit"
        track={{
          list_name: 'PASSWORD PROTECTED CONTENT',
          click_id: submitLabel,
          click_text: submitLabel,
        }}
      >
        <span className="inline-flex items-center gap-2">
          {submitLabel}
          {isBusy && (
            <CircularProgress
              // `inherit` keeps the spinner on the button's own text color, so it
              // works on every brand theme without hardcoding a palette value.
              color="inherit"
              size={16}
              aria-hidden
              data-testid="password-gate-spinner"
            />
          )}
        </span>
      </Button>
    </form>
  )
}

export default PasswordGateForm
